<script setup lang="ts">
import { computed, provide, ref, useTemplateRef } from "vue";

import { drawActions } from "@/action/drawActions";
import { drawShapeSelected } from "@/analytics/events/draw";
import { trackEvent } from "@/analytics/track";
import { boardKey } from "@/components/board/injectKeys";
import { useDragScroll } from "@/components/gestures/useDragScroll";
import { shapeKey } from "@/components/injectKeys";
import ActionMenu from "@/components/menu/ActionMenu/ActionMenu.vue";
import { relativeClientCoord, relativeFromViewport } from "@/math/coordinate-utils";
import {
  clampRelativeCoord,
  distance2,
  isHorizontal,
  minus,
  nearestLinePoint,
  plus,
} from "@/math/coordinates";
import { snapDistance } from "@/model/Settings";
import type {
  CoordinateComponent,
  Line,
  LineComponent,
  RelativeCoordinate,
  ViewportCoordinate,
} from "@/model/coordinates";
import { oppositeLineComponent, relativeCoord } from "@/model/coordinates";
import type { Shape } from "@/model/shape";
import cssValue from "@/model/variable.module.scss";
import { useBoardStore } from "@/store/board";
import { useContextMenuStore } from "@/store/contextMenu";
import type { Selected } from "@/store/draw";
import { useDrawStore } from "@/store/draw";
import { usePanModeStore } from "@/store/panMode";
import { useUserStore } from "@/store/user";
import { clientCoord } from "@/utils/dom/coordinates";

import DrawContextMenu from "./internal/DrawContextMenu.vue";
import ShapeActionMenu from "./internal/ShapeActionMenu.vue";
import TextLine from "./internal/TextLine.vue";
import TextRect from "./internal/TextRect.vue";
import { useDrawActionMenu } from "./internal/useDrawActionMenu";
import { usePointerPosition } from "./internal/usePointerPosition";
import { useSvgShapes } from "./internal/useSvgShapes";

const width = 1600;
const height = 900;

provide(boardKey, ref(useBoardStore().currentBoard()));

const rootRef = useTemplateRef("root");

const { line, rectAroundPoint, rectAlongLine } = useSvgShapes(width, height);
const anchor = rectAroundPoint(8);
const editableLine = rectAlongLine(2);

const { pointerPos } = usePointerPosition(rootRef);

useDragScroll(rootRef);

type Anchor = LineComponent | "line";

const isPanModeActive = computed(() => usePanModeStore().active);
const selected = computed(() => useDrawStore().selected);
const selectedShape = computed(() => selected.value?.shape);
// a shape is only persisted on pointerup, so it has no id while it is being drawn
const drawnShape = computed(() =>
  selected.value && !selected.value.shape.id ? selected.value.shape : undefined,
);
const board = computed(() => useBoardStore().currentBoard());
const active = computed(() => useDrawStore().active);
// pan mode, visualize and observers freeze the board, so shapes must not be grabbable
const editable = computed(() => active.value && useUserStore().isAllowed("edit"));
const zIndex = computed(() => (active.value ? board.value.maxZ + 1 : cssValue.zIndexBoard));
const pointerEvents = computed(() => (active.value ? "all" : "none"));

provide(shapeKey, selectedShape);
const actionMenu = useDrawActionMenu(selectedShape);

function shapeDown(shape: Shape, anchor: Anchor, e: PointerEvent) {
  trackEvent(drawShapeSelected());
  useDrawStore().selected = {
    shape,
    anchor,
    offset: {
      p0: minus(shape.p0, relativeClientCoord(e)),
      p1: minus(shape.p1, relativeClientCoord(e)),
    },
  };
}

function up(e: PointerEvent) {
  if (!selected.value?.anchor || isDragOverMenu(e)) return;

  const shape = selected.value.shape;
  selected.value.anchor = null;
  if (shape.id) {
    drawActions.editShape("mouse", shape);
    return;
  }
  void drawActions.addShape("mouse", shape).then((stored) => {
    // addShape deduplicates against the store, so keep the selection on the stored shape
    if (selected.value?.shape === shape) {
      selected.value.shape = stored;
    }
  });
}

function isDragOverMenu(e: PointerEvent) {
  return e.type === "pointerleave" && actionMenu.isInMenu(e.relatedTarget as Node);
}

function move(event: PointerEvent) {
  if (selected.value?.anchor) {
    moveShape(selected.value.shape, selected.value, relativeClientCoord(event));
    event.stopPropagation();
    actionMenu.updateMenu();
  } else if (canStartShape() && pointerPos.down) {
    startShape(pointerPos.down);
  }
}

function moveShape(shape: Shape, selected: Selected, pos: RelativeCoordinate) {
  if (selected.anchor === "line") {
    shape.p0 = clampRelativeCoord(plus(selected.offset.p0, pos));
    shape.p1 = clampRelativeCoord(plus(selected.offset.p1, pos));
  } else if (selected.anchor) {
    const fixPoint = shape[oppositeLineComponent(selected.anchor)];
    shape[selected.anchor] =
      Math.abs(fixPoint.x - pos.x) < Math.abs(fixPoint.y - pos.y)
        ? relativeCoord(fixPoint.x, pos.y)
        : relativeCoord(pos.x, fixPoint.y);
  }

  for (const other of board.value.shapes) {
    snap(shape, other);
  }
}

function snap(shape: Shape, other: Shape) {
  if (
    other.id !== shape.id &&
    other.type === "line" &&
    isHorizontal(shape) !== isHorizontal(other)
  ) {
    snapPoint("p0");
    snapPoint("p1");
  }

  function snapPoint(point: LineComponent) {
    const n = nearestLinePoint(shape[point], other);
    if (distance2(n.point, shape[point]) < snapDistance) {
      const comp = components(shape);
      if (n.t < 0 || n.t > 1) {
        // near the end of the other line -> snap the whole line
        shape.p0[comp.fix] = n.point[comp.fix];
        shape.p1[comp.fix] = n.point[comp.fix];
      }
      // snap the near point only
      shape[point][comp.flex] = n.point[comp.flex];
    }
  }
}

function components(line: Line<RelativeCoordinate>): {
  fix: CoordinateComponent;
  flex: CoordinateComponent;
} {
  return isHorizontal(line) ? { fix: "y", flex: "x" } : { fix: "x", flex: "y" };
}

function canStartShape() {
  return editable.value && !selected.value?.anchor;
}

function startShape(pos: ViewportCoordinate) {
  const shape: Shape = {
    id: "",
    type: "line",
    fixed: false,
    p0: relativeFromViewport(pos),
    p1: relativeFromViewport(pos),
  };
  useDrawStore().selected = { shape, anchor: "p1" };
}

function isSelected(shape: Shape) {
  return shape.id === selected.value?.shape.id;
}

function contextMenu(e: MouseEvent) {
  useContextMenuStore().open(DrawContextMenu, { position: clientCoord(e) });
}
</script>

<template>
  <ActionMenu :controller="actionMenu">
    <ShapeActionMenu />
  </ActionMenu>
  <svg
    ref="root"
    class="draw-layer"
    :class="{ active, 'pan-mode': isPanModeActive }"
    :viewBox="`0 0 ${width} ${height}`"
    preserveAspectRatio="none"
    stroke="currentColor"
    :style="{ zIndex, pointerEvents }"
    aria-hidden="true"
    @pointermove.capture="move"
    @pointerup="up"
    @pointerleave="up"
    @contextmenu="contextMenu"
  >
    <g
      v-for="shape in board.shapes"
      :id="`shape-${shape.id}`"
      :key="shape.id"
      :class="{ selected: isSelected(shape), border: shape.border }"
    >
      <TextLine v-if="!editable || shape.fixed" v-bind="{ ...line(shape), ...shape.label }" />
      <template v-else>
        <TextRect
          class="line"
          v-bind="{ ...editableLine(shape), ...shape.label }"
          @pointerdown="shapeDown(shape, 'line', $event)"
        />
        <rect
          class="anchor"
          v-bind="anchor(shape.p0)"
          @pointerdown="shapeDown(shape, 'p0', $event)"
        />
        <rect
          class="anchor"
          v-bind="anchor(shape.p1)"
          @pointerdown="shapeDown(shape, 'p1', $event)"
        />
      </template>
    </g>
    <TextRect v-if="drawnShape" class="line" v-bind="editableLine(drawnShape)" />
  </svg>
</template>

<style lang="scss">
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/variables/a11y" as colors-a11y;
@use "sass:color";

.draw-layer {
  position: absolute;
  width: 100% * variables.$fake-zoom;
  height: 100% * variables.$fake-zoom;
  fill: none;

  // this is not inside line element because it would break the screenshot function
  // if different elements have different stroke/fill make sure screenshot still works
  color: colors-a11y.$board-border;
  stroke-width: 1;

  .border {
    stroke-width: 2; // make borders double width because half of it is cut off
  }

  &.active {
    background-color: colors.$background-alpha-inactive;

    line {
      stroke: colors.$main;
    }
  }

  &.pan-mode {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  text {
    fill: colors-a11y.$board-border;
    stroke-width: 0.7;
    stroke-linejoin: round; // avoid strange buggy effects rendering text
    font-size: 8.5px;
  }

  rect {
    &.line {
      cursor: move;
      fill: colors.$main;
      stroke: transparent;
      stroke-width: 8;
    }

    &.anchor {
      cursor: move;
      stroke: colors.$main;
      stroke-width: 2;
      rx: 2;
    }
  }

  g.selected {
    text {
      stroke: colors.$main;
      fill: colors.$main;
    }

    rect.anchor {
      fill: colors.$main;
    }
  }
}
</style>
